import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import { cancelButtonStyle } from "../../styles/buttonStyles";
import { useUploadImage } from "../../hooks/useImages";

interface ImageCropModalProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  imageTitle?: string;
  onCropComplete: (croppedImageId: string) => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  onClose,
  imageUrl,
  imageTitle,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadImage = useUploadImage();

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom: number) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("No 2d context");
    }

    // Set canvas size to match the crop area
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped portion of the image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to create blob"));
          }
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) {
      return;
    }

    setIsUploading(true);
    try {
      // Create cropped image blob
      const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);
      const croppedFile = new File(
        [croppedBlob],
        `${imageTitle || "cover"}-cropped.jpg`,
        { type: "image/jpeg" }
      );

      // Upload cropped image
      const timestamp = Date.now();
      const publicId = `gallery-cover-${timestamp}`;

      const result = await uploadImage.mutateAsync({
        file: croppedFile,
        public_id: publicId,
        folder: "mariah-photography/covers",
        title: `${imageTitle || "Cover"} (Cropped)`,
        alt_text: `Cover image for ${imageTitle || "gallery"}`,
      });

      // Extract image ID from the result
      // The result from useUploadImage is ImageUploadResponse which has { success: boolean, data: ImageUploadData }
      // ImageUploadData has both id and public_id fields
      const imageId =
        (result as any).data?.id || (result as any).data?.public_id || publicId;
      onCropComplete(imageId);
      onClose();
    } catch (error) {
      console.error("Failed to crop and upload image:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 4, pt: 4, pb: 2 }}>Crop Cover Image</DialogTitle>
      <DialogContent
        sx={{
          px: 0,
          py: 2,
          position: "relative",
          height: { xs: "400px", sm: "500px" },
          backgroundColor: "#000",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={3 / 2}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            cropShape="rect"
            showGrid={false}
          />
        </Box>
      </DialogContent>
      <DialogContent sx={{ px: 4, py: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Zoom
          </Typography>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 3, px: 4, py: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isUploading}
          disableRipple
          sx={cancelButtonStyle}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="text"
          disableRipple
          disabled={isUploading || !croppedAreaPixels}
          sx={{
            textTransform: "none",
            border: "none",
            boxShadow: "none",
            px: 3,
            py: 1,
          }}
        >
          {isUploading ? "Uploading..." : "Save Cropped Image"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
